import { withNamespaces } from "react-i18next";
import { useState } from "react";
import { connect } from "react-redux";
import {
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Collapse,
    Progress,
    Row,
    Spinner,
} from "reactstrap";
import {
    CONFORMITY_OBLIGATION_SOURCE_MAP,
    PROVIDER_CONFORMITY_STATUS
} from "src/modules/data-protection/constants";
import useExportCeConformityPdf from "src/modules/data-protection/pages/admin/software/details/components/export-ce-conformity-pdf/hooks/useExportCeConformityPdf";
import ExportWaitingModal from "src/modules/data-protection/pages/admin/software/details/components/export-ce-conformity-pdf/components/ExportWaitingModal";

const getObligationSourceCaption = (obligations) => {
    const labels = obligations
        .map((obligation) => CONFORMITY_OBLIGATION_SOURCE_MAP[obligation.source]?.label)
        .filter(Boolean);

    return [...new Set(labels)][0] || null;
};

const ObligationItem = ({ label, variant, gapReason }) => {
    const isGap = variant === "gap";

    return (
        <div
            className={`rounded p-3 ${isGap ? "" : "bg-light"}`}
            style={
                isGap
                    ? { backgroundColor: "rgba(255, 193, 7, 0.08)" }
                    : undefined
            }
        >
            <div className="d-flex align-items-center gap-2">
                <i
                    className={`${
                        isGap
                            ? "ri-error-warning-line text-warning"
                            : "ri-checkbox-circle-line text-success"
                    } mt-1 fs-5`}
                />

                <div>
                    <div className="fw-medium">{label}</div>

                    {isGap && gapReason && (
                        <small className="text-muted d-block mt-1">
                            {gapReason}
                        </small>
                    )}
                </div>
            </div>
        </div>
    );
};

const ComplianceCeConformity = ({
    t,
    softwareDetailsQuery,
    assetId,
    organization,
    user,
}) => {
    const [completedOpen, setCompletedOpen] = useState(false);

    const {
        exportPDFInprocess,
        exportPDFObjectUrl,
        handleExport,
        clearExport,
    } = useExportCeConformityPdf({
        t,
        softwareId: assetId,
        organization,
        user,
    });

    if (softwareDetailsQuery.isFetching) {
        return null;
    }

    const softwareDetails = softwareDetailsQuery.data;

    if (!softwareDetails?.provider_conformity_required) {
        return null;
    }

    const {
        provider_conformity_status,
        conformity_answer_completion_percent = 0,
        conformity_gaps_count = 0,
        eu_database_registration_id,
        conformity_obligations = []
    } = softwareDetails;

    const statusConfig = PROVIDER_CONFORMITY_STATUS[provider_conformity_status] || {
        color: "secondary",
        label: provider_conformity_status || "Unknown"
    };

    const openGaps = conformity_obligations.filter(
        (obligation) => obligation.status === "gap"
    );
    const completedRequirements = conformity_obligations.filter(
        (obligation) => obligation.status === "met"
    );
    const obligationSourceCaption = getObligationSourceCaption(conformity_obligations);
    const showEuRegistrationId =
        provider_conformity_status === "placed_on_eu_market"
        && !!eu_database_registration_id;

    return (
        <Card className="mb-4" id="ce-conformity">
            <CardHeader>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="d-flex align-items-center">
                        <i className="ri-shield-check-line me-2 fs-5" />

                        <h5 className="mb-0">
                            {t("CE Conformity")}
                        </h5>
                    </div>

                    <Button
                        color="primary"
                        size="sm"
                        onClick={handleExport}
                        disabled={exportPDFInprocess || !assetId}
                        className="d-flex align-items-center"
                    >
                        {exportPDFInprocess ? (
                            <Spinner size="sm" className="me-2" />
                        ) : (
                            <i className="ri-file-pdf-line me-1" />
                        )}
                        {t("Export PDF")}
                    </Button>
                </div>
            </CardHeader>

            <CardBody>
                <div className="border rounded p-3 mb-4 bg-light">
                    <Row className="g-3 align-items-center">
                        <Col md={4} sm={6}>
                            <div className="text-muted small mb-1">
                                {t("CE Status")}
                            </div>

                            <Badge
                                color={statusConfig.color}
                                className={`bg-${statusConfig.color} text-white`}
                                pill
                            >
                                {t(statusConfig.label)}
                            </Badge>
                        </Col>

                        <Col md={4} sm={6}>
                            <div className="text-muted small mb-1">
                                {t("Open Gaps")}
                            </div>

                            <div
                                className={`fs-5 fw-semibold ${
                                    conformity_gaps_count > 0 ? "text-warning" : ""
                                }`}
                            >
                                {conformity_gaps_count}
                            </div>
                        </Col>

                        <Col md={4} sm={12}>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="text-muted small">
                                    {t("Questionnaire Completion")}
                                </span>

                                <span className="fw-medium small">
                                    {conformity_answer_completion_percent}%
                                </span>
                            </div>

                            <Progress
                                value={conformity_answer_completion_percent}
                                max={100}
                                color={
                                    conformity_answer_completion_percent === 100
                                        ? "success"
                                        : "primary"
                                }
                            />
                        </Col>

                        {showEuRegistrationId && (
                            <Col xs={12}>
                                <div className="text-muted small mb-1">
                                    {t("EU Database Registration ID")}
                                </div>

                                <div className="fw-medium text-break">
                                    {eu_database_registration_id}
                                </div>
                            </Col>
                        )}
                    </Row>
                </div>

                {conformity_obligations.length > 0 && (
                    <div>
                        <div className="mb-3">
                            <div className="fw-semibold">
                                {t("Open Gaps")}
                                {openGaps.length > 0 && (
                                    <Badge
                                        color="warning"
                                        className="bg-warning text-dark ms-2"
                                        pill
                                    >
                                        {openGaps.length}
                                    </Badge>
                                )}
                            </div>

                            {obligationSourceCaption && (
                                <small className="text-muted d-block mt-1">
                                    {t(obligationSourceCaption)}
                                </small>
                            )}
                        </div>

                        {openGaps.length > 0 ? (
                            <div className="d-flex flex-column mb-4">
                                <Row>
                                    {openGaps.map((obligation) => (
                                        <Col 
                                            sm="12" md="6" 
                                            className="mb-3"
                                            key={obligation.code}
                                        >
                                            <ObligationItem
                                                label={t(obligation.label)}
                                                variant="gap"
                                                gapReason={obligation.gap_reason}
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        ) : (
                            <p className="text-muted small mb-4">
                                {t("No open gaps. All tracked requirements are currently met.")}
                            </p>
                        )}

                        {completedRequirements.length > 0 && (
                            <div>
                                <button
                                    type="button"
                                    className="btn btn-link text-decoration-none p-0 d-flex align-items-center gap-1 fw-semibold"
                                    onClick={() => setCompletedOpen((prev) => !prev)}
                                    aria-expanded={completedOpen}
                                >
                                    <i
                                        className={
                                            completedOpen
                                                ? "ri-arrow-up-s-line"
                                                : "ri-arrow-down-s-line"
                                        }
                                    />

                                    {t("Completed Requirements")}

                                    <Badge
                                        color="light"
                                        className="text-dark ms-1 bg-light"
                                        pill
                                    >
                                        {completedRequirements.length}
                                    </Badge>
                                </button>

                                <Collapse isOpen={completedOpen}>
                                    <div className="d-flex flex-column gap-2 mt-3">
                                        <Row>
                                            {completedRequirements.map((obligation) => (
                                                <Col 
                                                    sm="12" 
                                                    md="6" 
                                                    className="mb-3"
                                                    key={obligation.code}
                                                >
                                                    <ObligationItem
                                                        label={t(obligation.label)}
                                                        variant="met"
                                                    />
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                </Collapse>
                            </div>
                        )}
                    </div>
                )}
            </CardBody>

            <ExportWaitingModal
                isOpen={exportPDFInprocess}
                toggle={clearExport}
                pdfObjectUrl={exportPDFObjectUrl}
            />
        </Card>
    );
};

const mapStatetoProps = (state) => {
    const { user } = state.Login;
    const { Organization } = state;

    return {
        user,
        organization: Organization,
    };
};

export default withNamespaces()(
    connect(mapStatetoProps)(ComplianceCeConformity)
);
